import { ArrowLeftRight, CircleAlert, CircleCheck, CircleDashed, LoaderCircle, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from '@krp/ui-kit'

import type { SourceFreshnessVm } from '../model/types'
import './sourcesStrip.css'

const KIND_ICONS: Readonly<Record<SourceFreshnessVm['kind'], ReactNode>> = {
  ok: <CircleCheck size={14} aria-hidden="true" />,
  wait: <LoaderCircle size={14} aria-hidden="true" />,
  error: <CircleAlert size={14} aria-hidden="true" />,
  idle: <CircleDashed size={14} aria-hidden="true" />,
}

/**
 * Список источников «Номенклатуры» (RP-SE13): откуда наполняется общий каталог. Заголовок несёт строка-триггер «Источники»
 * в шапке каталога, которая этот список раскрывает; строки приходят готовыми (`SourceRow`), пакет не знает, какие источники
 * бывают: их собирает binding из пакетов видов.
 */
export function SourcesStrip({ children }: { children: ReactNode }) {
  return (
    <ul className="platform-sources__list" aria-label="Источники" data-testid="platform-sources">
      {children}
    </ul>
  )
}

/**
 * Источник, у вида которого нет своей проверки каталога (например, 1С): его каталог сверяет «Синхронизировать» (RP-SY12).
 * Решение владельца 01.11.2026: кнопки проверки показываются на месте, но выключены, и под наведением объясняют, почему —
 * вместо того чтобы отсутствовать. Состояние различается значком и текстом, не цветом.
 */
export function PlainSourceRow({ id, name, kindTitle, tone = null }: { id: string; name: string; kindTitle: string; tone?: string | null }) {
  const reason = 'Каталог 1С сверяется кнопкой «Синхронизировать» на вкладке синхронизации.'
  return (
    <li className="platform-sources__row" data-testid={`platform-source-${id}`} {...(tone === null ? {} : { 'data-source': tone })}>
      <div className="platform-sources__what">
        <strong className="platform-sources__name">{name}</strong>
        <span className="platform-sources__kind">{kindTitle}</span>
      </div>
      <p className="platform-sources__status platform-sources__status--idle">
        <ArrowLeftRight size={14} aria-hidden="true" />
        <span>Сверяется синхронизацией</span>
      </p>
      <div className="platform-sources__actions" title={reason}>
        <Button
          variant="secondary"
          icon={<RefreshCw size={14} aria-hidden="true" />}
          disabled
          disabledReason={reason}
          actionId="platform.catalog.sources.check"
        >
          Проверить актуальность
        </Button>
        <Button variant="ghost" disabled disabledReason={reason} actionId="platform.catalog.sources.check-full">
          Проверить полностью
        </Button>
      </div>
    </li>
  )
}

/**
 * Один источник: название, сколько товаров, когда проверяли и чем кончилось. Состояние различается значком и словами, не цветом.
 * «Проверить актуальность» - проверка сейчас; «уже идёт» и «проверено только что» приходят в `note`, а не всплывают окном.
 */
export function SourceRow({
  source,
  onCheck,
  onCheckFull,
  tone = null,
}: {
  source: SourceFreshnessVm
  onCheck: (id: string) => void
  onCheckFull?: (id: string) => void
  /** Цвет вида источника (`data-source`); `null` — без цвета. */
  tone?: string | null
}) {
  return (
    <li className="platform-sources__row" data-testid={`platform-source-${source.id}`} {...(tone === null ? {} : { 'data-source': tone })}>
      <div className="platform-sources__what">
        <strong className="platform-sources__name">{source.name}</strong>
        <span className="platform-sources__kind">{source.kindTitle}</span>
        <span className="platform-sources__count">{source.productsText}</span>
      </div>
      <p className={`platform-sources__status platform-sources__status--${source.kind}`} aria-live="polite">
        {KIND_ICONS[source.kind]}
        <span>{source.statusText}</span>
      </p>
      {source.note !== null && (
        <p className="platform-sources__note" role="status">
          {source.note}
        </p>
      )}
      <div className="platform-sources__actions">
        <Button
          variant="secondary"
          icon={<RefreshCw size={14} aria-hidden="true" />}
          busy={source.busy}
          disabled={source.busy}
          onClick={() => onCheck(source.id)}
          actionId="platform.catalog.sources.check"
        >
          Проверить актуальность
        </Button>
        {onCheckFull !== undefined && (
          <Button
            variant="ghost"
            disabled={source.busy}
            onClick={() => onCheckFull(source.id)}
            actionId="platform.catalog.sources.check-full"
          >
            Проверить полностью
          </Button>
        )}
      </div>
    </li>
  )
}
